import MonsterSelect from './MonsterSelect';
import { useUploadCounter } from '../../hooks/useUploadCounter';
import { getMonsterImgPath } from '../../utils/monster';
import React, { useState, useEffect } from 'react';
import { isOperator } from '../../utils/permission';
import { TacticBuild, BuildRunes } from '../TacticBuild';
import { useBackHandler } from '../../hooks/useBackHandler';

interface UploadCounterProps {
  onUpload: (post: { id: number; title: string; image?: string; comment: string }) => void;
  author: string;
  onClose?: () => void;
  /**
   * **방덱을 미리 채워 연다.** 전적검색에서 "이 방덱 공략 쓰기" 로 들어올 때 쓴다 —
   * 방금 고른 방덱을 여섯 칸 중 뒤 세 칸에 넣어 두면 사람은 공덱만 고르면 된다.
   */
  initialDefense?: Array<{ name: string; attribute: string }>;
  /** 공덱도 미리 채운다 — 전투기록에서 "공략으로 쓰기" 로 들어올 때 */
  initialAttack?: Array<{ name: string; attribute: string }>;
  /** 몹마다 룬 세팅(공덱 세 칸 + 방덱 세 칸) — 전투기록의 리플레이 룬으로 셈해 채운다 */
  initialBuilds?: Array<TacticBuild | null>;
}

function UploadCounter({ onUpload, author, onClose, initialDefense, initialAttack, initialBuilds }: UploadCounterProps) {
  const {
    step, setStep,
    title, setTitle,
    image, setImage,
    comment, setComment,
    monsterResults, setMonsterResults,
    detecting, setDetecting,
    hoverIdx, setHoverIdx,
    editIdx, setEditIdx,
    selectedAttr, setSelectedAttr,
    selectedStar, setSelectedStar,
    monsterMapping, setMonsterMapping,
    miniModalIdx, setMiniModalIdx,
    ATTRIBUTES,
    handleImageChange,
    handlePaste,
    requestMonsterDetect,
    handleUpload,
    handleCorrect
  } = useUploadCounter(onUpload);
  
  // 일괄 업로드 관련 상태
  /**
   * 공략 추가는 **바로 작성 단계로 들어간다.**
   *
   * 예전에는 이미지 등록 → 몬스터 인식 → 작성 순서였다. 인식을 걷어낸 뒤로 이미지 등록
   * 단계는 아무 일도 하지 않는 관문만 남아서 그것까지 없앴다. 그래서 이미지 없는 한 장짜리
   * 상태로 시작한다 — 몹 여섯 칸은 작성 화면에서 직접 고른다.
   */
  const EMPTY_DECK: any[] = [null, null, null, null, null, null];
  const [batchImages, setBatchImages] = useState<string[]>(['']);
  const [batchIdx, setBatchIdx] = useState(0);
  /** 앞 세 칸이 공덱, 뒤 세 칸이 방덱이다 (`slice(0,3)` / `slice(3,6)`) */
  const [batchMonsterResults, setBatchMonsterResults] = useState<any[][]>(() => [
    initialDefense && initialDefense.length === 3
      ? [...(initialAttack && initialAttack.length === 3 ? initialAttack : [null, null, null]), ...initialDefense]
      : [...EMPTY_DECK]
  ]);
  const [batchTitles, setBatchTitles] = useState<string[]>(['']);
  const [batchComments, setBatchComments] = useState<string[]>(['']);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [batchTargetNickname, setBatchTargetNickname] = useState<string>('');
  const [batchNicknames, setBatchNicknames] = useState<string[]>(['']); // OCR로 인식된 닉네임들

  /** 작성 상태를 처음(이미지 없는 한 장)으로 되돌린다 */
  const resetBatch = () => {
    setBatchImages(['']);
    setBatchMonsterResults([[...EMPTY_DECK]]);
    setBatchTitles(['']);
    setBatchComments(['']);
    setBatchNicknames(['']);
    setBatchIdx(0);
  };

  // 유저 선택 상태를 부모에서 관리
  const [users, setUsers] = useState<any[]>([]);
  /** 관리 권한이 있는지. 직급이 아니라 권한으로 판단한다(사이트 운영자 포함) */
  const [canOperate, setCanOperate] = useState(false);

  // 유저 정보 가져오기
  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (meRes.ok) {
          const meData = await meRes.json();
          setCanOperate(isOperator(meData));
          setBatchTargetNickname(meData.nickname);
        }

        const usersRes = await fetch('/api/auth/users', { credentials: 'include' });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.users || []);
        }
      } catch (err) {
      }
    })();
  }, []);

  // 이미지 인덱스가 변경될 때마다 해당 이미지의 OCR 닉네임으로 자동 선택
  useEffect(() => {
    if (batchNicknames.length > batchIdx && batchNicknames[batchIdx]) {
      const ocrNickname = batchNicknames[batchIdx];
      const foundUser = users.find(u => u.nickname === ocrNickname);
      if (foundUser) {
        setBatchTargetNickname(ocrNickname);
      } else {
        // OCR 닉네임이 매칭되지 않으면 본인으로 자동 선택
        fetch('/api/auth/me', { credentials: 'include' })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data?.nickname) {
              setBatchTargetNickname(data.nickname);
            }
          })
          .catch(err => {});
      }
    }
  }, [batchIdx, batchNicknames, users]);

  // 뒤로 가기: 원본 이미지 보기 닫기 (쓰기 창 자체는 연 쪽이 등록한다)
  useBackHandler(imageModalOpen && !!batchImages[batchIdx], () => setImageModalOpen(false));

  return (
    <>
    <div className="modal-backdrop" style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="modal" style={{ background: '#fff', borderRadius: 24, minWidth: 480, maxWidth: 680, width: '90vw', maxHeight: '90vh', overflowY: 'auto', padding: '24px 48px 48px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(102, 126, 234, 0.15)', position: 'relative', border: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <h2 style={{ fontWeight: 800, fontSize: 22, letterSpacing: -1, color: '#667eea', marginBottom: 0, marginTop: 0, textAlign: 'center', textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>공략 추가</h2>
        {batchImages.length > 0 && (
          <>
            {batchImages[0] && batchImages[0] !== '' && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap', maxWidth: '520px', margin: '0 auto 20px' }}>
                {batchImages.map((img, idx) => (
                  img && img !== '' && (
                    <div
                      key={idx}
                      style={{
                        border: idx === batchIdx ? '3px solid #667eea' : '2px solid rgba(200, 200, 200, 0.3)',
                        borderRadius: 10,
                        width: 48,
                        height: 48,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        opacity: idx === batchIdx ? 1 : 0.6,
                        transition: 'all 0.3s ease',
                        boxShadow: idx === batchIdx ? '0 4px 12px rgba(102, 126, 234, 0.4)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
                        transform: idx === batchIdx ? 'scale(1.1)' : 'scale(1)',
                      }}
                      onClick={() => {
                        if (idx === batchIdx) {
                          setImageModalOpen(true);
                        } else {
                          setBatchIdx(idx);
                        }
                      }}
                    >
                      <img loading="lazy" src={img} alt={`batch-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )
                ))}
              </div>
            )}
            <SingleUploadStep2
              key={batchIdx}
              image={batchImages[batchIdx]}
              initialMonsterResults={batchMonsterResults[batchIdx]}
              initialBuilds={batchIdx === 0 ? initialBuilds : undefined}
              title={batchTitles[batchIdx]}
              comment={batchComments[batchIdx]}
              setTitle={title => setBatchTitles(titles => titles.map((t, i) => i === batchIdx ? title : t))}
              setComment={comment => setBatchComments(comments => comments.map((c, i) => i === batchIdx ? comment : c))}
              setMonsterResults={results => setBatchMonsterResults(all => all.map((arr, i) => i === batchIdx ? results : arr))}
              onNicknameChange={(nickname) => setBatchTargetNickname(nickname)}
              users={users}
              canOperate={canOperate}
              selectedNickname={batchTargetNickname}
              onUploadDone={async () => {
                if (batchIdx < batchImages.length - 1) {
                  setBatchIdx(batchIdx + 1);
                } else {
                  await onUpload({ id: 0, title: '', comment: '' });
                  if (onClose) onClose();
                  resetBatch();
                }
              }}
            />
            <button
              style={{ width: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 800, fontSize: 17, border: 'none', borderRadius: 12, padding: '16px 0', marginTop: 24, cursor: 'pointer', boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)', transition: 'all 0.3s ease', letterSpacing: -0.5, display: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(102, 126, 234, 0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)'; }}
              onClick={async () => {
                let successCount = 0;
                let duplicateCount = 0;
                const duplicateTitles: string[] = [];

                for (let i = 0; i < batchImages.length; i++) {
                  const attackTeam = batchMonsterResults[i]?.slice(0, 3).map(m => ({ name: m?.name, attribute: m?.attribute }));
                  const defenseTeam = batchMonsterResults[i]?.slice(3, 6).map(m => ({ name: m?.name, attribute: m?.attribute }));
                  const body: any = {
                    title: batchTitles[i],
                    comment: batchComments[i],
                    attackTeam,
                    defenseTeam
                  };
                  // 선택된 유저 닉네임이 있으면 추가

                  try {
                    const response = await fetch('/api/posts/UploadCounter', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify(body)
                    });

                    const result = await response.json();

                    if (response.ok && result.success) {
                      successCount++;
                    } else if (response.status === 400 && result.error?.includes('동일한 공덱과 방덱')) {
                      duplicateCount++;
                      duplicateTitles.push(batchTitles[i]);
                    } else {
                    }
                  } catch (error) {
                  }
                }

                // 중복 실패가 있을 때만 메시지 표시
                if (duplicateCount > 0) {
                  alert(`업로드가 완료되었습니다. \n\n성공: ${successCount}개\n중복으로 인한 실패: ${duplicateCount}개\n\n중복된 게시글:\n${duplicateTitles.join('\n')}`);
                }

                // 게시글 목록 새로고침을 위해 onUpload 콜백 호출
                await onUpload({ id: 0, title: '', comment: '' });
                resetBatch();
              }}
            >
              전체 업로드
            </button>
          </>
        )}
        <button onClick={() => onClose ? onClose() : onUpload({ id: 0, title: '', comment: '' })} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(200, 200, 200, 0.15)', border: 'none', fontSize: 28, color: '#999', cursor: 'pointer', fontWeight: 700, transition: 'all 0.2s', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(200, 200, 200, 0.25)'; e.currentTarget.style.color = '#666'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(200, 200, 200, 0.15)'; e.currentTarget.style.color = '#999'; }}>×</button>
      </div>
    </div>
    {/* 이미지 원본 보기 모달 */}
    {imageModalOpen && batchImages[batchIdx] && batchImages[batchIdx] !== '' && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.9)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        onClick={() => setImageModalOpen(false)}
      >
        <img loading="lazy"
          src={batchImages[batchIdx]}
          alt="원본 이미지"
          style={{
            maxWidth: '90%',
            maxHeight: '90%',
            objectFit: 'contain',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
          }}
        />
        <button
          onClick={() => setImageModalOpen(false)}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            background: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            fontSize: 32,
            color: '#333',
            cursor: 'pointer',
            fontWeight: 700,
            width: 50,
            height: 50,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
        >
          ×
        </button>
      </div>
    )}
    </>
  );
}

interface SingleUploadStep2Props {
  image: string;
  initialMonsterResults?: any[];
  onUploadDone: () => void | Promise<void>;
  title?: string;
  comment?: string;
  setTitle?: (t: string) => void;
  setComment?: (c: string) => void;
  setMonsterResults?: (arr: any[]) => void;
  onNicknameChange?: (nickname: string) => void;
  users?: any[];
  canOperate?: boolean;
  selectedNickname?: string;
  /** 몹마다 룬 세팅 — 공덱 0~2, 방덱 3~5 */
  initialBuilds?: Array<TacticBuild | null>;
}

const SingleUploadStep2: React.FC<SingleUploadStep2Props> = ({
  image,
  initialMonsterResults,
  onUploadDone,
  title = '',
  comment = '',
  setTitle,
  setComment,
  setMonsterResults,
  onNicknameChange,
  users: propsUsers = [],
  canOperate: propsCanOperate = false,
  selectedNickname: propsSelectedNickname = '',
  initialBuilds
}) => {
  // 기존 UploadCounter의 useUploadCounter 훅을 재사용하되, image만 강제 주입
  const {
    detecting, setDetecting,
    hoverIdx, setHoverIdx,
    editIdx, setEditIdx,
    selectedAttr, setSelectedAttr,
    selectedStar, setSelectedStar,
    monsterMapping, setMonsterMapping,
    miniModalIdx, setMiniModalIdx,
    ATTRIBUTES,
    requestMonsterDetect,
    handleUpload,
    handleCorrect
  } = useUploadCounter(() => { onUploadDone(); });
  // title, comment, monsterResults는 props 우선 사용
  const [localTitle, localSetTitle] = React.useState<string>(title);
  const [localComment, localSetComment] = React.useState<string>(comment);
  const [localMonsterResults, localSetMonsterResults] = React.useState<any[]>(initialMonsterResults || [null, null, null, null, null, null]);
  React.useEffect(() => { localSetTitle(title); }, [title]);
  React.useEffect(() => { localSetComment(comment); }, [comment]);
  React.useEffect(() => { if (initialMonsterResults) localSetMonsterResults(initialMonsterResults); }, [initialMonsterResults]);

  // 유저 선택 관련 상태 - props에서 받거나 로컬 상태 사용
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [localSelectedNickname, setLocalSelectedNickname] = React.useState<string>(propsSelectedNickname);

  React.useEffect(() => {
    setLocalSelectedNickname(propsSelectedNickname);
  }, [propsSelectedNickname]);

  // 드롭다운 외부 클릭 시 닫기
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // 외부 setter가 있으면 동기화
  const handleTitleChange = (v: string) => { localSetTitle(v); if (setTitle) setTitle(v); };
  const handleCommentChange = (v: string) => { localSetComment(v); if (setComment) setComment(v); };
  const handleMonsterResultsChange = (arr: any[]) => { localSetMonsterResults(arr); if (setMonsterResults) setMonsterResults(arr); };

  // 몬스터 교정 핸들러
  /**
   * 몹마다 적은 룬 세팅 — 칸 번호는 몹 칸과 같다(공덱 0~2, 방덱 3~5). 몹을 바꾸면 그 칸 세팅은 지운다
   * (다른 몹의 수치가 남으면 엉뚱한 공략이 된다).
   */
  const [localBuilds, setLocalBuilds] = React.useState<Array<TacticBuild | null>>(
    // 방덱은 룬 세팅을 적지 않는다(사용자 지시) — 공덱 세 칸만
    () => [0, 1, 2, 3, 4, 5].map((i) => (i < 3 && initialBuilds && initialBuilds[i]) || null)
  );
  /**
   * [공략] / [룬 정보] 탭 — 공략 상세와 같다(사용자 지시). 룬은 [룬 정보] 탭에서 공덱 몹을 골라 고친다.
   * 고친 세팅은 [작성 완료] 때 함께 올라간다.
   */
  const [tab, setTab] = React.useState<'tactic' | 'runes'>('tactic');
  const [runeIdx, setRuneIdx] = React.useState(0);

  // 뒤로 가기: 몹 고르기 창 닫기
  useBackHandler(miniModalIdx !== null, () => setMiniModalIdx(null));

  const handleCorrectMonster = (idx: number, monster: any) => {
    const newResults = [...localMonsterResults];
    // useUploadCounter의 handleCorrect 호출하여 교정 정보 전송
    handleCorrect(idx, monster, localMonsterResults);
    newResults[idx] = { ...monster };
    localSetMonsterResults(newResults);
    setLocalBuilds((bs) => bs.map((b, i) => (i === idx ? null : b)));
    if (setMonsterResults) setMonsterResults(newResults);
  };

  return (
    <>
      {/* 유저 선택 + 제목 입력 (같은 줄) */}
      <div style={{ display: 'flex', gap: 12, width: '100%', marginBottom: 20, alignItems: 'flex-start' }}>
        {/* 제목 입력 */}
        <div style={{ flex: 1 }}>
          <input
            type="text"
            placeholder="제목을 입력해 주세요."
            value={localTitle}
            onChange={e => handleTitleChange(e.target.value)}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: '#fff',
              border: '2px solid #e0e0e0',
              borderRadius: 12,
              padding: '12px 16px',
              fontSize: 18,
              fontWeight: 500,
              color: '#222',
              outline: 'none',
              fontFamily: 'Pretendard, Noto Sans KR, sans-serif',
              transition: 'all 0.3s ease',
              boxShadow: 'none',
              height: 52
            }}
            autoComplete="off"
            spellCheck={false}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#667eea'; (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e0e0e0'; (e.target as HTMLInputElement).style.boxShadow = 'none'; }}
          />
        </div>
      </div>
      {/* 몬스터 인식 결과 표시 (공덱/방덱) */}
      <div style={{ width: '100%', marginBottom: 28, paddingBottom: 28, borderBottom: '1px solid #e0e0e0' }}>
        {localMonsterResults && localMonsterResults.length === 6 && (
          <>
            {/* 공덱/방덱 레이블 */}
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 8 }}>
              <div style={{
                fontSize: 14,
                fontWeight: 800,
                color: '#1976d2',
                letterSpacing: -0.5,
                textAlign: 'center'
              }}>공덱</div>
              <div style={{
                fontSize: 14,
                fontWeight: 800,
                color: '#d32f2f',
                letterSpacing: -0.5,
                textAlign: 'center'
              }}>방덱</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, justifyContent: 'center', alignItems: 'flex-start' }}>
            {/* 공격덱 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                {localMonsterResults.slice(0, 3).map((m, idx) => {
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  if (imgPath) {
                    return (
                      <div key={idx} onClick={() => { if (tab === 'runes') setRuneIdx(idx); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4, ...(tab === 'runes' ? { cursor: 'pointer', borderRadius: 10, outline: runeIdx === idx ? '3px solid #ffb300' : 'none', outlineOffset: 2 } : {}) }}>
                        <div style={{ position: 'relative', width: 50, height: 50 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 50,
                              height: 50,
                              borderRadius: 8,
                              padding: 0,
                              background: 'linear-gradient(270deg, #1976d2, #42a5f5, #90caf9, #1976d2)',
                              backgroundSize: '400% 400%',
                              animation: 'blue-border 3s linear infinite',
                              position: 'relative',
                            }}
                            onMouseEnter={() => setHoverIdx(idx)}
                            onMouseLeave={() => setHoverIdx(null)}
                          >
                            <img loading="lazy"
                              src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 44,
                                height: 44,
                                objectFit: 'contain',
                                borderRadius: 7,
                                margin: 3,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #1976d222',
                                transition: 'opacity 0.3s',
                                opacity: hoverIdx === idx ? undefined : 1,
                                animation: hoverIdx === idx ? 'img-opacity-flash 1s infinite alternate' : 'none',
                              }}
                            />
                            {hoverIdx === idx && (
                              <span
                                onClick={() => setMiniModalIdx(idx)}
                                style={{
                                  position: 'absolute', top: 0, right: 0, width: 16, height: 16,
                                  background: 'rgba(25,118,210,0.9)', color: '#fff', borderRadius: '50%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', fontWeight: 'bold', fontSize: 12, zIndex: 2
                                }}
                                title="교정"
                              >×</span>
                            )}
                            {miniModalIdx === idx && (
                              <MonsterSelect
                                open={true}
                                monsterMapping={monsterMapping}
                                onClose={() => setMiniModalIdx(null)}
                                onSelect={monster => {
                                  handleCorrectMonster(idx, monster);
                                  setMiniModalIdx(null);
                                }}
                                defaultAttr={m?.attribute}
                                defaultStar={m?.star}
                              />
                            )}
                          </span>
                        </div>
                        {imgPath ? (
                          <>
                            <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                          </>
                        ) : null}
                      </div>
                    );
                  } else {
                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                        <button
                          style={{ width: 50, height: 50, borderRadius: 8, border: '1.5px dashed #1976d2', background: '#eee', color: '#1976d2', fontSize: 20, cursor: 'pointer' }}
                          onClick={() => setMiniModalIdx(idx)}
                        >
                          +
                        </button>
                        {miniModalIdx === idx && (
                          <MonsterSelect
                            open={true}
                            monsterMapping={monsterMapping}
                            onClose={() => setMiniModalIdx(null)}
                            onSelect={monster => {
                              handleCorrectMonster(idx, monster);
                              setMiniModalIdx(null);
                            }}
                          />
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
            {/* VS */}
            <div style={{
              fontSize: 20,
              fontWeight: 600,
              color: '#667eea',
              letterSpacing: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // 덱은 위쪽에 맞춘다(아래에 이름·[룬] 단추가 붙으면 길이가 달라진다) — VS 는 아이콘 줄 가운데
              alignSelf: 'flex-start',
              height: 50
            }}>VS</div>
            {/* 방어덱 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                {localMonsterResults.slice(3, 6).map((m, idx) => {
                  const realIdx = idx + 3;
                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                  if (imgPath) {
                    return (
                      <div key={realIdx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                        <div style={{ position: 'relative', width: 50, height: 50 }}>
                          <span
                            style={{
                              display: 'inline-block',
                              width: 50,
                              height: 50,
                              borderRadius: 8,
                              padding: 0,
                              background: 'linear-gradient(270deg, #d32f2f, #ff5252, #ffb4b4, #d32f2f)',
                              backgroundSize: '400% 400%',
                              animation: 'red-border 3s linear infinite',
                              position: 'relative',
                            }}
                            onMouseEnter={() => setHoverIdx(realIdx)}
                            onMouseLeave={() => setHoverIdx(null)}
                          >
                            <img loading="lazy"
                              src={`/mobs/v2/${imgPath.replace(/\\/g, '/')}`}
                              alt={displayName}
                              style={{
                                width: 44,
                                height: 44,
                                objectFit: 'contain',
                                borderRadius: 7,
                                margin: 3,
                                background: '#fff',
                                display: 'block',
                                boxShadow: '0 2px 8px #d32f2f22',
                                transition: 'opacity 1s',
                                opacity: hoverIdx === realIdx ? undefined : 1,
                                animation: hoverIdx === realIdx ? 'img-opacity-flash 1s infinite alternate' : 'none',
                              }}
                            />
                            {hoverIdx === realIdx && (
                              <span
                                onClick={() => setMiniModalIdx(realIdx)}
                                style={{
                                  position: 'absolute', top: 0, right: 0, width: 16, height: 16,
                                  background: 'rgba(211,47,47,0.9)', color: '#fff', borderRadius: '50%',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', fontWeight: 'bold', fontSize: 12, zIndex: 2
                                }}
                                title="교정"
                              >×</span>
                            )}
                            {miniModalIdx === realIdx && (
                              <MonsterSelect
                                open={true}
                                monsterMapping={monsterMapping}
                                onClose={() => setMiniModalIdx(null)}
                                onSelect={monster => {
                                  handleCorrectMonster(realIdx, monster);
                                  setMiniModalIdx(null);
                                }}
                                defaultAttr={m?.attribute}
                                defaultStar={m?.star}
                              />
                            )}
                          </span>
                        </div>
                        {imgPath ? (
                          <div style={{ fontSize: 11, color: '#333', fontWeight: 600, textAlign: 'center', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                        ) : null}
                      </div>
                    );
                  } else {
                    return (
                      <div key={realIdx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto', gap: 4 }}>
                        <button
                          style={{ width: 50, height: 50, borderRadius: 8, border: '1.5px dashed #d32f2f', background: '#eee', color: '#d32f2f', fontSize: 20, cursor: 'pointer' }}
                          onClick={() => setMiniModalIdx(realIdx)}
                        >
                          +
                        </button>
                        {miniModalIdx === realIdx && (
                          <MonsterSelect
                            open={true}
                            monsterMapping={monsterMapping}
                            onClose={() => setMiniModalIdx(null)}
                            onSelect={monster => {
                              handleCorrectMonster(realIdx, monster);
                              setMiniModalIdx(null);
                            }}
                          />
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
          </>
        )}
      </div>
      {/* [공략] / [룬 정보] 탭 — 공략 상세와 같다 */}
      <div style={{ display: 'flex', gap: 6, width: '100%', marginBottom: 14, borderBottom: '1px solid #eee' }}>
        {([['tactic', '공략'], ['runes', '룬 정보']] as Array<['tactic' | 'runes', string]>).map(([k, label]) => (
          <button
            key={k} type="button"
            onClick={() => {
              setTab(k);
              // 룬 정보로 오면 몹이 있는 첫 공덱 칸을 고른다
              if (k === 'runes') {
                const first = [0, 1, 2].find((i) => localMonsterResults[i]?.name);
                setRuneIdx(first ?? 0);
              }
            }}
            style={{
              padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 15, fontWeight: 800, color: tab === k ? '#667eea' : '#aaa',
              borderBottom: tab === k ? '3px solid #667eea' : '3px solid transparent', marginBottom: -1
            }}
          >{label}</button>
        ))}
      </div>
      {tab === 'runes' && (
        <div style={{ width: '100%', marginBottom: 8 }}>
          {/* 위 공덱 칸을 누르면 그 몹으로 바뀐다. 고친 세팅은 [작성 완료] 때 함께 저장된다 */}
          <BuildRunes
            build={localBuilds[runeIdx] || null}
            mob={localMonsterResults[runeIdx]?.name ? localMonsterResults[runeIdx] : null}
            mapping={monsterMapping}
            isMobile={false}
            onSave={(nb) => setLocalBuilds((bs) => bs.map((x, i) => (i === runeIdx ? nb : x)))}
            applyLabel="적용"
          />
        </div>
      )}
      {tab === 'tactic' && (
      <textarea
        placeholder="내용을 입력하세요."
        value={localComment}
        onChange={e => handleCommentChange(e.target.value)}
        style={{
          width: '100%',
          minHeight: 270,
          marginTop: 0,
          border: '2px solid #e0e0e0',
          borderRadius: 16,
          padding: '24px 28px',
          fontSize: 17,
          fontWeight: 400,
          color: '#333',
          background: '#fff',
          lineHeight: 1.8,
          boxSizing: 'border-box',
          fontFamily: 'Pretendard, Noto Sans KR, sans-serif',
          outline: 'none',
          resize: 'vertical',
          transition: 'all 0.3s ease',
          boxShadow: 'none'
        }}
        onFocus={e => { (e.target as HTMLTextAreaElement).style.borderColor = '#667eea'; (e.target as HTMLTextAreaElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)'; }}
        onBlur={e => { (e.target as HTMLTextAreaElement).style.borderColor = '#e0e0e0'; (e.target as HTMLTextAreaElement).style.boxShadow = 'none'; }}
      />
      )}
      <button
        onClick={async () => {
          const attackTeam = localMonsterResults?.slice(0, 3).map(m => ({ name: m?.name, attribute: m?.attribute }));
          const defenseTeam = localMonsterResults?.slice(3, 6).map(m => ({ name: m?.name, attribute: m?.attribute }));
          const body: any = {
            title: localTitle,
            comment: localComment,
            attackTeam,
            defenseTeam,
            builds: { attack: localBuilds.slice(0, 3) }
          };

          try {
            const response = await fetch('/api/posts/UploadCounter', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(body)
            });

            const result = await response.json();

            if (response.ok && result.success) {
              await onUploadDone();
            } else if (response.status === 400 && result.error?.includes('동일한 공덱과 방덱')) {
              alert('동일한 공덱과 방덱이 이미 존재합니다.');
            } else {
              alert('업로드 실패: ' + (result.error || '알 수 없는 오류'));
            }
          } catch (error) {
            alert('업로드 중 오류가 발생했습니다.');
          }
        }}
        style={{
          width: '100%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 800,
          fontSize: 17,
          border: 'none',
          borderRadius: 12,
          padding: '16px 0',
          marginTop: 24,
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
          transition: 'all 0.3s ease',
          letterSpacing: -0.5
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(102, 126, 234, 0.5)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)'; }}
      >
        작성 완료
      </button>
    </>
  );
};

export default UploadCounter; 